import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import './LancarMovimentacoes.css'

const formatarMoeda = (v) =>
  Number(v) != null && !isNaN(Number(v))
    ? Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0,00'

const formatarData = (dataStr) => {
  if (!dataStr) return '–'
  const s = String(dataStr).split('T')[0]
  const [y, m, d] = s.split('-')
  return d && m && y ? `${d}/${m}/${y}` : s
}

const LancarMovimentacoes = ({ onBack, userId, userEmpresa, onVerFinanceiro }) => {
  const hoje = new Date().toISOString().split('T')[0]
  const [tipos, setTipos] = useState([])
  const [movimentacoes, setMovimentacoes] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState({ text: '', type: '' })
  const [tipoId, setTipoId] = useState('')
  const [quantidade, setQuantidade] = useState(1)
  const [valorUnitario, setValorUnitario] = useState('')
  const [dataCadastro, setDataCadastro] = useState(hoje)
  const [observacao, setObservacao] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [confirmacaoSucesso, setConfirmacaoSucesso] = useState(false)

  const loadTipos = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('tipo_entrada_saida')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome')
      if (error) throw error
      setTipos(data || [])
      if (data?.length && !tipoId) setTipoId(data[0].id)
    } catch (err) {
      console.error('Erro ao carregar tipos:', err)
    }
  }, [])

  const loadMovimentacoes = useCallback(async () => {
    try {
      setLoading(true)
      const trintaDiasAtras = new Date()
      trintaDiasAtras.setDate(trintaDiasAtras.getDate() - 30)
      const dataInicio = trintaDiasAtras.toISOString().split('T')[0]
      let query = supabase
        .from('movimentacao')
        .select(`
          id,
          quantidade,
          valor_unitario,
          total,
          data_cadastro,
          observacao,
          tipo_entrada_saida(id, nome)
        `)
        .gte('data_cadastro', dataInicio)
        .order('data_cadastro', { ascending: false })
        .order('id', { ascending: false })
        .limit(50)
      if (userEmpresa?.id) {
        const { data: usuariosEmpresa } = await supabase
          .from('usuarios_sistema')
          .select('id')
          .eq('empresa_id', userEmpresa.id)
        const ids = (usuariosEmpresa || []).map((u) => u.id)
        if (ids.length > 0) query = query.in('id_responsavel', ids)
        else {
          setMovimentacoes([])
          setLoading(false)
          return
        }
      }
      const { data, error } = await query
      if (error) throw error
      setMovimentacoes(data || [])
    } catch (err) {
      console.error('Erro ao carregar movimentações:', err)
      setMovimentacoes([])
    } finally {
      setLoading(false)
    }
  }, [userEmpresa?.id])

  useEffect(() => { loadTipos() }, [loadTipos])
  useEffect(() => { loadMovimentacoes() }, [loadMovimentacoes])
  useEffect(() => {
    if (tipos.length > 0 && !tipoId) setTipoId(tipos[0].id)
  }, [tipos, tipoId])

  const limparForm = useCallback(() => {
    setValorUnitario('')
    setObservacao('')
    setQuantidade(1)
    setDataCadastro(new Date().toISOString().split('T')[0])
    setTipoId(tipos[0]?.id ?? '')
    setMsg({ text: '', type: '' })
    setConfirmacaoSucesso(false)
  }, [tipos])

  const abrirModal = () => {
    limparForm()
    setConfirmacaoSucesso(false)
    setShowModal(true)
  }

  const fecharModal = () => {
    setShowModal(false)
    setConfirmacaoSucesso(false)
    setMsg({ text: '', type: '' })
  }

  const getStatusNovoId = useCallback(async () => {
    const { data } = await supabase.from('movimentacao_status').select('id').eq('codigo', 0).single()
    return data?.id ?? null
  }, [])

  const handleLancar = async (e) => {
    e.preventDefault()
    const idTipo = tipoId ? parseInt(tipoId, 10) : null
    const qtd = parseInt(quantidade, 10) || 1
    const valorUnit = valorUnitario ? parseFloat(String(valorUnitario).replace(',', '.')) : 0
    if (!idTipo) { setMsg({ text: 'Selecione o tipo.', type: 'error' }); return }
    if (!dataCadastro) { setMsg({ text: 'Informe a data.', type: 'error' }); return }
    if (qtd < 1) { setMsg({ text: 'Quantidade deve ser maior que zero.', type: 'error' }); return }
    if (!userId) { setMsg({ text: 'Usuário não identificado.', type: 'error' }); return }
    setSaving(true)
    setMsg({ text: '', type: '' })
    try {
      const { data: inserted, error: errInsert } = await supabase
        .from('movimentacao')
        .insert([{
          id_responsavel: userId,
          id_tipo_entrada_saida: idTipo,
          quantidade: qtd,
          valor_unitario: valorUnit,
          data_cadastro: dataCadastro,
          status: 0,
          pago: false,
          observacao: observacao.trim() || null
        }])
        .select('id')
        .single()
      if (errInsert) throw errInsert
      const statusNovoId = await getStatusNovoId()
      if (inserted?.id && statusNovoId) {
        await supabase.from('mov_historico').insert({
          fatura: inserted.id,
          id_responsavel: userId,
          tipo_mov: statusNovoId,
          data_mov: new Date().toISOString()
        })
      }
      setConfirmacaoSucesso(true)
      limparForm()
      loadMovimentacoes()
    } catch (err) {
      setMsg({ text: err.message || 'Erro ao lançar movimentação.', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="lancar-mov">
      <div className="lancar-mov-header">
        <button type="button" className="lancar-mov-btn-back" onClick={onBack} aria-label="Voltar">← Voltar</button>
        <h1 className="lancar-mov-title">Lançar Movimentações</h1>
      </div>
      <p className="lancar-mov-descricao">
        Registre entradas e saídas na tabela de movimentações. Clique no botão abaixo para abrir o formulário.
      </p>

      <button type="button" className="lancar-mov-btn-abrir-modal" onClick={abrirModal}>
        <span className="lancar-mov-btn-icone">💰</span>
        Nova movimentação
      </button>

      {/* Modal Nova movimentação */}
      {showModal && (
        <div className="lancar-mov-modal-overlay" onClick={fecharModal}>
          <div className="lancar-mov-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="lancar-mov-modal-titulo"><span className="lancar-mov-card-icone">💰</span> Nova movimentação</h2>

            {confirmacaoSucesso ? (
              <div className="lancar-mov-modal-confirmacao">
                <p className="lancar-mov-modal-confirmacao-msg">Movimentação lançada com sucesso!</p>
                <div className="lancar-mov-modal-confirmacao-botoes">
                  <button type="button" className="lancar-mov-btn-lancar" onClick={abrirModal}>
                    Lançar outra
                  </button>
                  <button type="button" className="lancar-mov-btn-fechar-modal" onClick={fecharModal}>
                    Fechar
                  </button>
                </div>
              </div>
            ) : (
              <>
                {msg.text && <div className={`lancar-mov-msg lancar-mov-msg-${msg.type}`} role="alert">{msg.text}</div>}
                <form onSubmit={handleLancar} className="lancar-mov-form">
                  <div className="row g-2">
                    <div className="col-12">
                      <label className="form-label">Tipo *</label>
                      <select className="form-select" value={tipoId} onChange={(e) => setTipoId(e.target.value)} required>
                        <option value="">Selecione</option>
                        {tipos.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Quantidade *</label>
                      <input type="number" min={1} className="form-control" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} required />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Valor unit. (R$) *</label>
                      <input type="text" inputMode="decimal" placeholder="0,00" className="form-control" value={valorUnitario} onChange={(e) => setValorUnitario(e.target.value)} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Data *</label>
                      <input type="date" className="form-control" value={dataCadastro} onChange={(e) => setDataCadastro(e.target.value)} required />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Observação</label>
                      <input type="text" className="form-control" placeholder="Ex: descrição ou referência" value={observacao} onChange={(e) => setObservacao(e.target.value)} />
                    </div>
                    <div className="col-12 d-flex flex-wrap gap-2 justify-content-end pt-2">
                      <button type="button" className="lancar-mov-btn-limpar" onClick={limparForm}>Limpar</button>
                      <button type="submit" className="lancar-mov-btn-lancar" disabled={saving}>{saving ? 'Lançando...' : 'Lançar'}</button>
                      <button type="button" className="lancar-mov-btn-fechar-modal" onClick={fecharModal}>Cancelar</button>
                    </div>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <section className="lancar-mov-lista">
        <div className="lancar-mov-lista-header">
          <h2 className="lancar-mov-lista-titulo">Últimas movimentações</h2>
          {onVerFinanceiro && <button type="button" className="lancar-mov-link-financeiro" onClick={onVerFinanceiro}>Ver todas no Financeiro →</button>}
        </div>
        {loading ? <p className="lancar-mov-loading">Carregando...</p> : (
          <div className="lancar-mov-tabela-wrap">
            <table className="lancar-mov-tabela">
              <thead><tr><th>Data</th><th>Tipo</th><th className="lancar-mov-th-valor">Valor</th><th>Observação</th></tr></thead>
              <tbody>
                {movimentacoes.length === 0 ? <tr><td colSpan={4} className="lancar-mov-empty">Nenhuma movimentação nos últimos 30 dias. Clique em &quot;Nova movimentação&quot; para lançar.</td></tr> : movimentacoes.map((m) => <tr key={m.id}><td>{formatarData(m.data_cadastro)}</td><td>{m.tipo_entrada_saida?.nome ?? '–'}</td><td className="lancar-mov-valor">R$ {formatarMoeda(m.total)}</td><td className="lancar-mov-obs">{m.observacao || '–'}</td></tr>)}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

export default LancarMovimentacoes
